import React from 'react';
import { usePublicWebSocket } from '../../hooks/usePublicWebSocket';
import { useLogoStyle } from '../../hooks/useLogoStyle';
import { TeamLogo } from '../TeamLogo';

/**
 * Scoreboard Top Overlay
 * 
 * Horizontal top bar with:
 * - Line 1: Team logos + short names + scores
 * - Line 2: Clock + Period (centered)
 * 
 * Designed for OBS/vMix overlay
 * URL: /watch/:matchId?layout=scoreboard-top
 * 
 * @param {Object} wsData - WebSocket data from parent (required when used in AllLayersLayout)
 * @param {number} matchId - Match ID (used when rendering standalone)
 */
export const ScoreboardTop = ({ wsData, matchId }) => {
  // Use wsData from props if provided, otherwise call hook (for standalone use)
  const hookData = usePublicWebSocket(matchId);
  const { connected, match, score, clock, formationsVisible } = wsData || hookData;
  
  // Get styling from settings
  const { logoStyle, scoreboardSettings, loading: styleLoading } = useLogoStyle();

  // Loading state
  if (!connected || !match || styleLoading) {
    return (
      <div className="fixed top-0 left-0 right-0 p-5"
        style={{
          backgroundColor: scoreboardSettings?.backgroundColor || 'rgba(0,0,0,0.85)',
          color: scoreboardSettings?.textColor || '#FFFFFF'
        }}>
        <div className="text-center text-sm">Connecting...</div>
      </div>
    );
  }

  // Get alignment class based on settings (for flex container)
  const getAlignmentClass = () => {
    switch (scoreboardSettings?.alignment) {
      case 'left': return 'justify-start';
      case 'right': return 'justify-end';
      case 'center':
      default: return 'justify-center';
    }
  };

  const homeTeam = match.home_team;
  const awayTeam = match.away_team;

  // Get period label
  const getPeriodLabel = (half) => {
    switch (half) {
      case 1: return '1ST HALF';
      case 2: return '2ND HALF';
      case 3: return 'ET 1ST';
      case 4: return 'ET 2ND';
      default: return '1ST HALF';
    }
  };

  // Format clock time
  const formatTime = () => {
    const minutes = String(clock.minute).padStart(2, '0');
    const seconds = String(clock.second).padStart(2, '0');
    let time = `${minutes}:${seconds}`;
    
    // Add injury time if applicable
    if (clock.injuryTime > 0 && clock.minute >= 45) {
      time += ` +${clock.injuryTime}`;
    }
    
    return time;
  };

  return (
    <div className="fixed top-0 left-0 right-0 relative"
      style={{
        backgroundColor: scoreboardSettings?.backgroundColor || 'rgba(0,0,0,0.85)',
        color: scoreboardSettings?.textColor || '#FFFFFF'
      }}>

      {/* Main Content - Centered based on alignment settings */}
      <div className={`flex ${getAlignmentClass()}`}>
        <div className="px-5 py-4">

          {/* Line 1: Teams + Scores */}
        <div className="flex items-center gap-6 mb-3">
          {/* Home Team */}
          <div className="flex items-center gap-3">
            <TeamLogo team={homeTeam} size="w-20 h-20" logoStyle={logoStyle} />
            <div className="flex flex-col items-start">
              <span className="text-2xl font-bold">
                {homeTeam.short_name || homeTeam.name}
              </span>
              {formationsVisible && match?.home_formation && (
                <span className="text-sm text-gray-300">
                  {match.home_formation}
                </span>
              )}
            </div>
          </div>

          {/* Score */}
          <div className="flex items-center gap-4">
            <span className="text-5xl font-black">{score.home}</span>
            <span className="text-3xl font-bold text-gray-400">-</span>
            <span className="text-5xl font-black">{score.away}</span>
          </div>

          {/* Away Team */}
          <div className="flex items-center gap-3">
            <div className="flex flex-col items-end">
              <span className="text-2xl font-bold">
                {awayTeam.short_name || awayTeam.name}
              </span>
              {formationsVisible && match?.away_formation && (
                <span className="text-sm text-gray-300">
                  {match.away_formation}
                </span>
              )}
            </div>
            <TeamLogo team={awayTeam} size="w-20 h-20" logoStyle={logoStyle} />
          </div>
        </div>

        {/* Line 2: Clock + Period */}
        <div className="flex items-center gap-3">
          <span className="text-2xl font-mono font-bold">
            {formatTime()}
          </span>
          <span className="text-xl text-gray-400">•</span>
          <span className="text-xl font-semibold tracking-wide">
            {getPeriodLabel(clock.half)}
          </span>
        </div>

        </div>
      </div>
    </div>
  );
};

export default ScoreboardTop;
